import classNames from "classnames";
import { pickBy } from "lodash";
import { CSSProperties } from "react";
import * as styles from "./layout.less";

export type Spacing = 0 | 2 | 4 | 8 | 12 | 16 | 24 | 32;
export type Display = "flex" | "block" | "inline_block" | "inline_flex" | "none";
export type BorderWidth = 0 | 1;
export type SpacingValue =
    | Spacing
    | `${Spacing}`
    | `${Spacing} ${Spacing}`
    | `${Spacing} ${Spacing} ${Spacing}`
    | `${Spacing} ${Spacing} ${Spacing} ${Spacing}`;

export type CssProps = {
    margin?: SpacingValue;
    marginTop?: Spacing | "auto";
    marginBottom?: Spacing;
    marginLeft?: Spacing | "auto";
    marginRight?: Spacing | "auto";

    padding?: SpacingValue;
    paddingTop?: Spacing;
    paddingBottom?: Spacing;
    paddingLeft?: Spacing;
    paddingRight?: Spacing;

    border?: BorderWidth;
    borderTop?: BorderWidth;
    borderBottom?: BorderWidth;
    borderLeft?: BorderWidth;
    borderRight?: BorderWidth;
    borderRadius?: 0 | 4;
    boxSizing?: "borderBox" | "contentBox";
    borderColor?: CSSProperties["borderColor"];

    width?: CSSProperties["width"];
    maxWidth?: CSSProperties["maxWidth"];
    minWidth?: CSSProperties["maxWidth"];

    height?: CSSProperties["height"];
    maxHeight?: CSSProperties["maxHeight"];
    minHeight?: CSSProperties["minHeight"];

    display?: Display;

    overflow?: "auto" | "visible" | "hidden";

    /** color's HEX value */
    color?: CSSProperties["color"];
    /** background color's HEX value */
    background?: CSSProperties["backgroundColor"];

    // flex props
    gap?: Spacing;
    flexDirection?: "row" | "column" | "row_reverse";
    alignItems?: "center" | "start" | "end" | "stretch";
    alignSelf?: "center" | "start" | "end" | "stretch";
    justifyContent?: "start" | "center" | "end" | "spaceBetween" | "spaceEvenly";
    flexWrap?: "nowrap" | "wrap";

    flex?: `${1 | 0} ${1 | 0}`;

    style?: CSSProperties;
};

export const extractCssProps = <T extends CssProps>(allProps: T) => {
    if (allProps.margin != null || allProps.padding != null) {
        normalizeSpacingValue("margin", allProps);
        normalizeSpacingValue("padding", allProps);
    }

    let {
        margin,
        marginTop,
        marginBottom,
        marginLeft,
        marginRight,

        padding,
        paddingTop,
        paddingBottom,
        paddingLeft,
        paddingRight,

        border,
        borderTop,
        borderBottom,
        borderLeft,
        borderRight,
        borderRadius,
        borderColor,
        boxSizing,

        background,
        display,
        color,

        overflow,

        gap,
        flexDirection,
        flexWrap,
        alignItems,
        alignSelf,
        justifyContent,
        flex,

        width,
        minWidth,
        maxWidth,
        height,
        maxHeight,
        minHeight,
        style,

        ...props
    } = allProps;

    if (
        width != null ||
        minWidth != null ||
        maxWidth != null ||
        height != null ||
        minHeight != null ||
        maxHeight != null ||
        background != null ||
        color != null ||
        borderColor != null
    ) {
        const overrides = pickBy(
            { width, minWidth, maxWidth, height, minHeight, maxHeight, background, color },
            x => x !== undefined
        );

        if (borderColor) {
            overrides["--border-color"] = borderColor;
        }

        style = { ...style, ...overrides };
    }

    return {
        style,
        cssClass: classNames(
            margin && styles[`margin_${margin as Spacing}`],
            marginTop != null && styles[`margin_top_${marginTop}`],
            marginBottom != null && styles[`margin_bottom_${marginBottom}`],
            marginLeft != null && styles[`margin_left_${marginLeft}`],
            marginRight != null && styles[`margin_right_${marginRight}`],

            padding && styles[`padding_${padding as Spacing}`],
            paddingTop != null && styles[`padding_top_${paddingTop}`],
            paddingBottom != null && styles[`padding_bottom_${paddingBottom}`],
            paddingLeft != null && styles[`padding_left_${paddingLeft}`],
            paddingRight != null && styles[`padding_right_${paddingRight}`],

            border && styles.border,
            borderTop != null && styles[`borderTop_${borderTop}`],
            borderBottom != null && styles[`borderBottom_${borderBottom}`],
            borderLeft != null && styles[`borderLeft_${borderLeft}`],
            borderRight != null && styles[`borderRight_${borderRight}`],
            borderRadius && styles[`borderRadius_${borderRadius}`],
            boxSizing && styles[`boxSizing_${boxSizing}`],

            display && styles[`display_${display}`],

            overflow && styles[`overflow_${overflow}`],

            gap && styles[`gap_${gap}`],
            flexDirection && styles[`flex_direction_${flexDirection}`],
            alignItems && styles[`align_items_${alignItems}`],
            alignSelf && styles[`align_self_${alignSelf}`],
            justifyContent && styles[`justify_content_${justifyContent}`],
            flexWrap && styles[`flex_wrap_${flexWrap}`],
            flex && styles[`flex_${flex.replace(" ", "_")}` as keyof typeof styles]
        ),
        props,
    };
};

const normalizeSpacingValue = (key: "margin" | "padding", props: CssProps) => {
    let value = props[key];
    if (!value || typeof value === "number") {
        return;
    }

    if (typeof value === "string") {
        const values = value.split(" ") as unknown as Spacing[];
        if (values.length <= 1) {
            return;
        }

        props[key] = null;
        props[`${key}Top`] = values[0];
        props[`${key}Bottom`] = values.length >= 3 ? values[2] : values[0];
        props[`${key}Right`] = values[1];
        props[`${key}Left`] = values[3] ?? values[1];
    }
};
